import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { Plus, Search, Phone, Mail, Landmark, Percent, IndianRupee, Edit, Trash2, Package, User, X, Filter } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BANKS = ["SBI", "HDFC", "ICICI", "Axis", "Kotak", "PNB", "Bank of Baroda", "Canara Bank", "Union Bank", "Other"];

const BankAgentsPage = () => {
  const { isAdmin } = useAuth();
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedBanks, setSelectedBanks] = useState([...BANKS]);
  const [showBankFilter, setShowBankFilter] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [viewingAgent, setViewingAgent] = useState(null);
  const [editingAgent, setEditingAgent] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    bank_name: '',
    products: '',
    payout_type: 'percentage',
    payout_percentage: '',
    payout_fixed: ''
  });

  // Toggle bank filter
  const toggleBank = (bank) => {
    setSelectedBanks(prev => prev.includes(bank) ? prev.filter(b => b !== bank) : [...prev, bank]);
  };

  useEffect(() => {
    fetchAgents();
  }, []);

  const fetchAgents = async () => {
    try {
      const response = await axios.get(`${API}/bank-agents`);
      setAgents(response.data);
    } catch (error) {
      toast.error('Error fetching bank agents');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      const payload = {
        ...formData,
        payout_percentage: parseFloat(formData.payout_percentage) || 0,
        payout_fixed: parseFloat(formData.payout_fixed) || 0
      };

      if (editingAgent) {
        await axios.put(`${API}/bank-agents/${editingAgent.id}`, payload);
        toast.success('Bank agent updated successfully');
      } else {
        await axios.post(`${API}/bank-agents`, payload);
        toast.success('Bank agent created successfully');
      }
      setIsDialogOpen(false);
      resetForm();
      fetchAgents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error saving bank agent');
    }
  };

  const handleEdit = (agent) => {
    setEditingAgent(agent);
    setFormData({
      name: agent.name,
      phone: agent.phone,
      email: agent.email || '',
      bank_name: agent.bank_name,
      products: agent.products || '',
      payout_type: agent.payout_type,
      payout_percentage: agent.payout_percentage?.toString() || '',
      payout_fixed: agent.payout_fixed?.toString() || ''
    });
    setIsDialogOpen(true);
  };

  const handleDeactivate = async (agentId) => {
    if (!window.confirm('Are you sure you want to deactivate this bank agent?')) return;
    try {
      await axios.delete(`${API}/bank-agents/${agentId}`);
      toast.success('Bank agent deactivated');
      fetchAgents();
    } catch (error) {
      toast.error('Error deactivating bank agent');
    }
  };

  const resetForm = () => {
    setEditingAgent(null);
    setFormData({
      name: '',
      phone: '',
      email: '',
      bank_name: '',
      products: '',
      payout_type: 'percentage',
      payout_percentage: '',
      payout_fixed: ''
    });
  };

  const filteredAgents = agents.filter(agent => {
    const matchesSearch = agent.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      agent.phone?.includes(searchTerm) ||
      agent.bank_name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesBank = selectedBanks.some(b => agent.bank_name?.toLowerCase().includes(b.toLowerCase())) || selectedBanks.includes('Other');
    return matchesSearch && matchesBank;
  });

  const formatCurrency = (amount) => `₹${amount?.toLocaleString('en-IN') || 0}`;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin h-8 w-8 border-4 border-brand-navy border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div data-testid="bank-agents-page" className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-brand-navy font-heading">Bank Agents</h1>
          <p className="text-slate-500 mt-1">Manage bank agent contacts and payout structure</p>
        </div>
        {isAdmin && (
          <Button
            onClick={() => { resetForm(); setIsDialogOpen(true); }}
            data-testid="create-bank-agent-btn"
            className="bg-brand-navy text-white hover:bg-brand-navy/90 shadow-lg"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Bank Agent
          </Button>
        )}
      </div>

      {/* Search */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-4">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Search by name, phone or bank..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              data-testid="search-bank-agents"
              className="pl-10"
            />
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-0">
          {/* Bank Filter Panel */}
          {showBankFilter && (
            <div className="border-b p-4 bg-blue-50">
              <div className="flex items-center justify-between mb-2">
                <Label className="font-semibold text-slate-700">Select Banks:</Label>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => setSelectedBanks([...BANKS])}>All</Button>
                  <Button size="sm" variant="outline" onClick={() => setSelectedBanks([])}>Clear</Button>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {BANKS.map(bank => (
                  <label key={bank} className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border cursor-pointer text-xs ${selectedBanks.includes(bank) ? 'bg-blue-100 border-blue-300' : 'bg-white opacity-50'}`}>
                    <input type="checkbox" checked={selectedBanks.includes(bank)} onChange={() => toggleBank(bank)} className="w-3 h-3" />
                    {bank}
                  </label>
                ))}
              </div>
            </div>
          )}
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="font-semibold text-slate-600">Name</TableHead>
                <TableHead className="font-semibold text-slate-600 cursor-pointer hover:bg-slate-100" onClick={() => setShowBankFilter(!showBankFilter)}>
                  <div className="flex items-center gap-1">
                    Bank
                    <Filter className={`h-3 w-3 ${selectedBanks.length < BANKS.length ? 'text-brand-navy' : 'text-slate-400'}`} />
                  </div>
                </TableHead>
                <TableHead className="font-semibold text-slate-600">Phone</TableHead>
                <TableHead className="font-semibold text-slate-600">Product Description</TableHead>
                <TableHead className="font-semibold text-slate-600">Payout</TableHead>
                {isAdmin && <TableHead className="font-semibold text-slate-600 text-right">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredAgents.map((agent) => (
                <TableRow key={agent.id} className="hover:bg-slate-50/50">
                  <TableCell>
                    <button
                      onClick={() => setViewingAgent(agent)}
                      className="font-medium text-brand-navy hover:text-brand-gold hover:underline cursor-pointer text-left"
                      data-testid={`view-agent-${agent.id}`}
                    >
                      {agent.name}
                    </button>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Landmark className="h-4 w-4 text-slate-400" />
                      {agent.bank_name}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Phone className="h-3 w-3 text-slate-400" />
                      {agent.phone}
                    </div>
                  </TableCell>
                  <TableCell className="max-w-[250px]">
                    {agent.products ? (
                      <span className="text-sm text-slate-600 line-clamp-2">{agent.products}</span>
                    ) : '-'}
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-col gap-1">
                      {agent.payout_percentage > 0 && (
                        <span className="flex items-center gap-1 text-sm">
                          <Percent className="h-3 w-3 text-purple-500" />
                          {agent.payout_percentage}%
                        </span>
                      )}
                      {agent.payout_fixed > 0 && (
                        <span className="flex items-center gap-1 text-sm">
                          <IndianRupee className="h-3 w-3 text-blue-500" />
                          {formatCurrency(agent.payout_fixed)}
                        </span>
                      )}
                      {agent.payout_percentage === 0 && agent.payout_fixed === 0 && '-'}
                    </div>
                  </TableCell>
                  {isAdmin && (
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => handleEdit(agent)}
                          data-testid={`edit-agent-${agent.id}`}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="text-red-500 hover:text-red-700"
                          onClick={() => handleDeactivate(agent.id)}
                          data-testid={`deactivate-agent-${agent.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))}
              {filteredAgents.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-slate-400">
                    No bank agents found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-brand-navy">
              {editingAgent ? 'Edit Bank Agent' : 'Add New Bank Agent'}
            </DialogTitle>
            <DialogDescription>
              {editingAgent ? 'Update bank agent information' : 'Enter bank agent details and payout structure'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Agent Name</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  data-testid="input-agent-name"
                  placeholder="Enter name"
                />
              </div>
              <div className="space-y-2">
                <Label>Bank Name</Label>
                <Input
                  value={formData.bank_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, bank_name: e.target.value }))}
                  data-testid="input-agent-bank"
                  placeholder="Enter bank name"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Phone Number</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                  data-testid="input-agent-phone"
                  placeholder="Enter phone"
                />
              </div>
              <div className="space-y-2">
                <Label>Email (Optional)</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  data-testid="input-agent-email"
                  placeholder="Enter email"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Product List Description</Label>
              <Input
                value={formData.products}
                onChange={(e) => setFormData(prev => ({ ...prev, products: e.target.value }))}
                data-testid="input-agent-products"
                placeholder="e.g., Home Loan, Personal Loan, Business Loan, LAP"
              />
            </div>
            <div className="space-y-2">
              <Label>Payout Type</Label>
              <Select
                value={formData.payout_type}
                onValueChange={(v) => setFormData(prev => ({ ...prev, payout_type: v }))}
              >
                <SelectTrigger data-testid="select-payout-type">
                  <SelectValue placeholder="Select payout type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="percentage">Percentage of Loan Amount</SelectItem>
                  <SelectItem value="fixed">Fixed Amount per File</SelectItem>
                  <SelectItem value="both">Both (Percentage + Fixed)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {(formData.payout_type === 'percentage' || formData.payout_type === 'both') && (
              <div className="space-y-2">
                <Label>Payout Percentage (%)</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={formData.payout_percentage}
                  onChange={(e) => setFormData(prev => ({ ...prev, payout_percentage: e.target.value }))}
                  data-testid="input-payout-percentage"
                  placeholder="e.g., 0.5"
                />
              </div>
            )}
            {(formData.payout_type === 'fixed' || formData.payout_type === 'both') && (
              <div className="space-y-2">
                <Label>Fixed Payout Amount (₹)</Label>
                <Input
                  type="number"
                  value={formData.payout_fixed}
                  onChange={(e) => setFormData(prev => ({ ...prev, payout_fixed: e.target.value }))}
                  data-testid="input-payout-fixed"
                  placeholder="e.g., 5000"
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={handleSubmit}
              data-testid="submit-agent-btn"
              className="bg-brand-navy text-white hover:bg-brand-navy/90"
              disabled={!formData.name || !formData.phone || !formData.bank_name}
            >
              {editingAgent ? 'Update Agent' : 'Add Agent'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Agent Details Dialog */}
      <Dialog open={!!viewingAgent} onOpenChange={() => setViewingAgent(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-brand-navy flex items-center gap-2">
              <User className="h-5 w-5" />
              Agent Details
            </DialogTitle>
          </DialogHeader>
          {viewingAgent && (
            <div className="space-y-4 py-4">
              {/* Agent Name & Bank */}
              <div className="flex items-center gap-4 p-4 bg-brand-navy/5 rounded-lg">
                <div className="w-14 h-14 bg-brand-navy rounded-full flex items-center justify-center">
                  <span className="text-xl font-bold text-white">
                    {viewingAgent.name?.charAt(0).toUpperCase()}
                  </span>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-brand-navy">{viewingAgent.name}</h3>
                  <p className="text-slate-500 flex items-center gap-1">
                    <Landmark className="h-4 w-4" />
                    {viewingAgent.bank_name}
                  </p>
                </div>
              </div>

              {/* Contact Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-xs text-slate-500 mb-1">Phone</p>
                  <p className="font-medium flex items-center gap-2">
                    <Phone className="h-4 w-4 text-emerald-500" />
                    {viewingAgent.phone}
                  </p>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-xs text-slate-500 mb-1">Email</p>
                  <p className="font-medium flex items-center gap-2">
                    <Mail className="h-4 w-4 text-blue-500" />
                    {viewingAgent.email || '-'}
                  </p>
                </div>
              </div>

              {/* Products */}
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-xs text-slate-500 mb-1">Product List Description</p>
                <p className="font-medium flex items-start gap-2">
                  <Package className="h-4 w-4 text-purple-500 mt-0.5" />
                  {viewingAgent.products || 'No products listed'}
                </p>
              </div>

              {/* Payout Info */}
              <div className="p-4 bg-brand-gold/10 rounded-lg">
                <p className="text-xs text-slate-500 mb-2">Payout Details</p>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-slate-600">Percentage</p>
                    <p className="text-xl font-bold text-brand-navy flex items-center gap-1">
                      <Percent className="h-4 w-4 text-purple-500" />
                      {viewingAgent.payout_percentage || 0}%
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-600">Fixed Amount</p>
                    <p className="text-xl font-bold text-brand-navy flex items-center gap-1">
                      <IndianRupee className="h-4 w-4 text-emerald-500" />
                      {formatCurrency(viewingAgent.payout_fixed || 0)}
                    </p>
                  </div>
                </div>
              </div>

              {/* Created Date */}
              <p className="text-xs text-slate-400 text-center">
                Added on: {new Date(viewingAgent.created_at).toLocaleDateString('en-IN', { 
                  day: 'numeric', month: 'short', year: 'numeric' 
                })}
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setViewingAgent(null)}>Close</Button>
            {isAdmin && (
              <Button
                onClick={() => { handleEdit(viewingAgent); setViewingAgent(null); }}
                className="bg-brand-navy text-white hover:bg-brand-navy/90"
              >
                <Edit className="h-4 w-4 mr-2" />
                Edit Agent
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BankAgentsPage;
